import { QuestionCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import styled from '@emotion/styled';

import { guideForPath } from '../../models/guides/pageGuides';
import PageGuidePanel, { GUIDES_RESET_EVENT, hasSeenGuide, markGuideSeen } from '../Help/PageGuidePanel';

interface PageHeaderProps {
  title: string;
  description?: string;
  className?: string;
  customComp?: React.ReactNode;
}

// Title, a one-line "what this page is for" (from models/guides/pageGuides,
// matched by route), and "How this page works" tips: open on a first visit,
// then one click away. Pages explain themselves without anyone to ask.
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  className,
  customComp,
}) => {
  const { pathname } = useLocation();
  const guideKey = pathname.split('/').filter(Boolean)[0] ?? '';
  const guide = guideForPath(pathname);
  const [showTips, setShowTips] = useState(false);

  useEffect(() => {
    setShowTips(!!guide && !hasSeenGuide(guideKey));
    const onReset = () => setShowTips(!!guide);
    window.addEventListener(GUIDES_RESET_EVENT, onReset);
    return () => window.removeEventListener(GUIDES_RESET_EVENT, onReset);
    // eslint-disable-next-line
  }, [guideKey]);

  const closeTips = () => {
    markGuideSeen(guideKey);
    setShowTips(false);
  };

  const summary = description ?? guide?.summary;

  return (
    <>
      <Container className={className}>
        <TextArea>
          <HeaderTitle>{title}</HeaderTitle>
          {summary && (
            <HeaderDescription>
              {summary}
              {guide && !showTips && (
                <TipsLink type='button' onClick={() => setShowTips(true)}>
                  <QuestionCircleOutlined /> How this page works
                </TipsLink>
              )}
            </HeaderDescription>
          )}
        </TextArea>
        {customComp && <div>{customComp}</div>}
      </Container>
      {guide && showTips && <PageGuidePanel guide={guide} onClose={closeTips} />}
    </>
  );
};

export default PageHeader;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  align-items: flex-end;

  padding-bottom: 4px;

  &.no-bottom-padding {
    padding-bottom: 0px;
  }
`;

const TextArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

// Page titles in the website's display face.
const HeaderTitle = styled.h1`
  margin: 0;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(24px, 2.4vw, 30px);
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: ${({ theme }) => theme.palette.HetheraBlack};
`;

const HeaderDescription = styled.div`
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
  max-width: 680px;
`;

const TipsLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 10px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 13px;
  color: var(--ok);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;
